import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  BookOpen, 
  Store, 
  Zap, 
  BrainCircuit, 
  Smartphone, 
  CheckCircle2,
  ArrowRight
} from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Helmet } from "react-helmet-async";

export default function LandingPage() {
  return (
    <div className="flex flex-col gap-20 pb-20">
      <Helmet>
        <title>Flashcard Learning Platform | Học thông minh cùng SRS</title>
        <meta name="description" content="Nền tảng học tập thông minh dựa trên phương pháp Lặp lại ngắt quãng (SRS)." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl opacity-30" />
        </div>

        <div className="flex flex-col items-center text-center gap-8 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20"
          >
            <Zap className="h-4 w-4 fill-primary" />
            <span>Học nhanh hơn, nhớ lâu hơn</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-5xl md:text-8xl font-black tracking-tight leading-[1.1]"
          >
            Đột phá <br /> <span className="text-primary italic">Tri thức</span> của bạn
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl md:text-2xl text-muted-foreground leading-relaxed px-4 font-medium"
          >
            Nền tảng học tập thông minh dựa trên phương pháp Lặp lại ngắt quãng (SRS). 
            Học mọi lúc, mọi nơi và làm chủ mọi kiến thức.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-4 mt-4"
          >
            <Link 
              to="/register" 
              className={cn(buttonVariants({ size: "lg", className: "h-16 px-10 text-xl font-bold rounded-2xl shadow-xl shadow-primary/20" }))}
            >
              Bắt đầu ngay <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link 
              to="/marketplace" 
              className={cn(buttonVariants({ variant: "outline", size: "lg", className: "h-16 px-10 text-xl font-bold rounded-2xl border-2" }))}
            >
              Khám phá cộng đồng
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container py-12 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: BrainCircuit,
              title: "Thuật toán SRS",
              description: "Tối ưu hóa khả năng ghi nhớ dài hạn bằng thuật toán SM-2, dự đoán chính xác thời điểm bạn bắt đầu quên để nhắc lại kịp thời."
            },
            {
              icon: Store,
              title: "Thư viện Cộng đồng",
              description: "Truy cập hàng ngàn bộ thẻ được chia sẻ từ cộng đồng, từ ngoại ngữ, y khoa cho đến các kỳ thi chuyên môn."
            },
            {
              icon: Smartphone,
              title: "Học mọi nơi",
              description: "Ứng dụng web hỗ trợ học đa nền tảng, nhắc nhở và đồng bộ hóa tức thì trên mọi thiết bị của bạn."
            }
          ].map((feature, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -5 }}
              className="group p-8 rounded-3xl border bg-card hover:border-primary/50 hover:shadow-2xl transition-all duration-300"
            >
              <div className="mb-6 inline-flex p-3 rounded-2xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                <feature.icon className="h-6 w-6" />
              </div>
              <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container py-24 mx-auto px-4 md:px-6">
        <div className="relative rounded-[3rem] bg-zinc-900 overflow-hidden p-12 md:p-24 text-center">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 blur-3xl rounded-full -translate-x-1/2 translate-y-1/2" />
          
          <div className="relative z-10 max-w-2xl mx-auto space-y-8">
            <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight">Sẵn sàng bứt phá trí nhớ?</h2>
            <p className="text-zinc-400 text-xl font-medium">
              Gia nhập hàng ngàn học viên đang làm chủ kiến thức thông qua phương pháp lặp lại có hệ thống.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/register" 
                className={cn(buttonVariants({ size: "lg", className: "w-full sm:w-auto h-16 px-10 text-xl font-bold bg-primary text-primary-foreground rounded-2xl" }))}
              >
                Tạo tài khoản miễn phí
              </Link>
              <div className="flex items-center gap-2 text-zinc-500 font-medium">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Không cần thẻ tín dụng
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
